import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { SearchResult } from "../../types/activity-message-types"

import { ChevronDown, ChevronUp, Eye, FileText } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { formatCitationDisplay } from "@/utils/citation-utils"

import { useCitationPreview } from "../../contexts/use-citation-preview"

type SearchResultsProps = {
  activity: SearchResult
  citationSection?: string
  demoMode?: boolean
}

const MAX_INITIAL_RESULTS = 6

export function SearchResults({ activity, citationSection, demoMode = false }: SearchResultsProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const { openPreview } = useCitationPreview()

  if (activity.results.length === 0) {
    return null
  }

  const handleViewDocument = (title: string, library: string) => {
    const citation = `${library}:${title}`
    openPreview(citation, citationSection || "")
  }

  const visibleResults = isExpanded
    ? activity.results
    : activity.results.slice(0, MAX_INITIAL_RESULTS)

  const hasMoreResults = activity.results.length > MAX_INITIAL_RESULTS

  return (
    <>
      <div className="ml-6 space-y-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {visibleResults.map((result) => {
            const citation = `${result.library}:${result.title}`
            const { fileNameWithoutPage } = formatCitationDisplay(citation)
            const labelName = result.display_title ?? result.title

            return (
              <div
                key={`${result.library}-${result.title}`}
                className="border-border/50 bg-muted/30 flex items-center justify-between gap-2 rounded-md border p-2 text-sm"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FileText className="text-muted-foreground size-3 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="truncate text-xs font-medium" title={labelName}>
                      {labelName}
                    </div>
                    <div
                      className="text-muted-foreground truncate text-xs"
                      title={fileNameWithoutPage}
                    >
                      {fileNameWithoutPage.split(": ")[0]}
                    </div>
                  </div>
                </div>
                {!demoMode && (
                  <Button
                    className="h-6 w-6 shrink-0 p-0"
                    size="sm"
                    title={t("answer.show_document")}
                    variant="ghost"
                    onClick={() => handleViewDocument(result.title, result.library)}
                  >
                    <Eye className="size-3" />
                  </Button>
                )}
              </div>
            )
          })}
        </div>

        {hasMoreResults && (
          <Button
            className="text-muted-foreground hover:text-foreground w-full text-xs"
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-1 size-3" />
                {t("arrowbutton.show_less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-1 size-3" />
                {t("arrowbutton.show_more")}
              </>
            )}
          </Button>
        )}
      </div>
    </>
  )
}
